import { useCallback, useEffect, useState } from 'react'
import {
  ApiError,
  api,
  can,
  refusalReason,
  shortDate,
  type OnboardingProfileDoc,
  type OnboardingProfileVersion,
  type OnboardingPromoteResult,
  type OnboardingStepDef,
} from '../lib/api'
import { ErrorBox, Loading } from '../components/DataTable'
import { Why } from '../components/ui'
import { Status, type Tone } from '../components/kit'
import { ReasonTip } from '../components/vendor/ReasonTip'
import '../styles/studio.css'
import '../styles/sweep.css'

// The ten steps a supplier goes through, as this organisation runs them.
//
// This is the approval matrix screen's shape rather than a third way of editing
// versioned tenant content: a list of versions, one open, a draft that is saved
// and checked, and a promotion that retires what it replaces and stamps a
// content hash. Somebody who has promoted a matrix already knows how to work
// this one.
//
// # THE TWO KNOBS, AND WHY THERE ARE ONLY TWO
//
// Per step: whether activation waits for it, and whether a person may record it
// when the product cannot see it. Everything else about a step is fixed,
// because everything else is what the product observes and that is not
// configuration, it is what the code can look at.
//
// The second knob is the one worth being careful about, and the API is careful
// about it in a way this screen has to say out loud: recording a step never
// makes activation easier. The gate on the Approval tab is untouched, and these
// steps are asked on top of it. So the worst this screen can do is let somebody
// satisfy a requirement this organisation added itself.
//
// # WHY A STEP THE PRODUCT WATCHES CANNOT BE MADE ASSERTION-ONLY
//
// The API refuses it, and the reason is not policy. If observation stopped
// counting, a vendor that had plainly been tiered would show as not tiered
// until somebody typed a sentence saying so, which is a screen that lies about
// a fact sitting next to it.

// A retired version is finished business and goes quiet.
const STATUS_WORD: Record<string, { word: string; tone: Tone }> = {
  DRAFT: { word: 'Draft', tone: 'info' },
  ACTIVE: { word: 'Active', tone: 'ok' },
  RETIRED: { word: 'Retired', tone: 'plain' },
}

function asApiError(err: unknown): ApiError {
  return err instanceof ApiError ? err : new ApiError(0, 'UNKNOWN', String(err))
}

// A step with no evidence code is one only a person can speak to. It is also
// the only kind this screen can add or remove: the ten the product watches are
// the ten it knows how to look at, and a tenant deleting one would be switching
// off a check rather than describing their process.
function isOwnStep(s: OnboardingStepDef): boolean {
  return !s.evidence
}

function blankStep(n: number): OnboardingStepDef {
  return {
    key: 'own_step_' + n,
    name: '',
    mandatory: true,
    satisfiedBy: 'ASSERTED',
    why: '',
  }
}

export function OnboardingProcess() {
  const [versions, setVersions] = useState<OnboardingProfileVersion[] | null>(null)
  const [listNote, setListNote] = useState('')
  const [shipped, setShipped] = useState<OnboardingProfileDoc | null>(null)
  const [error, setError] = useState<unknown>(null)

  const [openId, setOpenId] = useState<string | null>(null)
  const [version, setVersion] = useState<OnboardingProfileVersion | null>(null)
  const [versionError, setVersionError] = useState<ApiError | null>(null)
  const [draft, setDraft] = useState<OnboardingProfileDoc | null>(null)
  const [dirty, setDirty] = useState(false)
  const [problems, setProblems] = useState<string[]>([])

  const [busy, setBusy] = useState('')
  const [writeError, setWriteError] = useState<ApiError | null>(null)
  const [savedNote, setSavedNote] = useState('')
  const [promoted, setPromoted] = useState<OnboardingPromoteResult | null>(null)
  const [subject, setSubject] = useState('')

  const load = useCallback(() => {
    api
      .onboardingProfileVersions()
      .then((r) => {
        setVersions(r.versions)
        setListNote(r.note ?? '')
        setShipped(r.default ?? null)
        setError(null)
        setOpenId((prev) => prev ?? r.versions.find((v) => v.status === 'ACTIVE')?.id ?? r.versions[0]?.id ?? null)
      })
      .catch(setError)
  }, [])

  useEffect(load, [load])

  // Who is signed in, for the half of promotion that is not a capability:
  // whoever drafted a version may not be the one who puts it into service.
  useEffect(() => {
    let live = true
    api
      .me()
      .then((m) => {
        if (live) setSubject(m.subject)
      })
      .catch(() => undefined)
    return () => {
      live = false
    }
  }, [])

  const openVersion = useCallback((id: string) => {
    api
      .onboardingProfileVersion(id)
      .then((v) => {
        setVersion(v.version)
        setDraft(v.version.profile ?? { steps: [] })
        setProblems(v.problems)
        setDirty(false)
        setSavedNote('')
        setVersionError(null)
      })
      .catch((err) => setVersionError(asApiError(err)))
  }, [])

  useEffect(() => {
    if (openId) openVersion(openId)
  }, [openId, openVersion])

  function edit(index: number, patch: Partial<OnboardingStepDef>) {
    setDraft((prev) => {
      if (!prev) return prev
      const steps = prev.steps.map((s, i) => (i === index ? { ...s, ...patch } : s))
      return { steps }
    })
    setDirty(true)
    setSavedNote('')
  }

  function addOwnStep() {
    setDraft((prev) => (prev ? { steps: [...prev.steps, blankStep(prev.steps.length + 1)] } : prev))
    setDirty(true)
    setSavedNote('')
  }

  function removeStep(index: number) {
    setDraft((prev) => (prev ? { steps: prev.steps.filter((_, i) => i !== index) } : prev))
    setDirty(true)
    setSavedNote('')
  }

  function save() {
    if (!version || !draft) return
    setBusy('save')
    setWriteError(null)
    api
      .saveOnboardingProfile(version.id, { profile: draft })
      .then((r) => {
        setProblems(r.problems)
        setSavedNote(r.note)
        setDirty(false)
      })
      .catch((err) => setWriteError(asApiError(err)))
      .finally(() => setBusy(''))
  }

  function startDraft(fromVersionId?: string) {
    setBusy('draft')
    setWriteError(null)
    setPromoted(null)
    api
      .draftOnboardingProfile(fromVersionId ? { fromVersionId } : {})
      .then((r) => {
        load()
        setOpenId(r.id)
        openVersion(r.id)
      })
      .catch((err) => setWriteError(asApiError(err)))
      .finally(() => setBusy(''))
  }

  function promote() {
    if (!version) return
    setBusy('promote')
    setWriteError(null)
    api
      .promoteOnboardingProfile(version.id)
      .then((r) => {
        setPromoted(r)
        load()
        openVersion(version.id)
      })
      .catch((err) => setWriteError(asApiError(err)))
      .finally(() => setBusy(''))
  }

  if (error) return <ErrorBox error={error} what="The onboarding process" />
  if (!versions) return <Loading what="the onboarding process" />

  const isDraft = version?.status === 'DRAFT'
  const mayWrite = can('programme.write')
  const mayPromote = can('vendor.approve')
  // The three walls on promotion, said at the button rather than after it. The
  // third is the one people are surprised by: configuring a tenant and admitting
  // a supplier are different jobs, so the person who wrote this usually cannot
  // put it into service.
  const promoteRefusal = !isDraft
    ? 'Only a draft can be promoted.'
    : !mayPromote
      ? refusalReason('vendor.approve')
      : version?.createdBy === subject
        ? 'You drafted this version. A change to what a supplier must have been through needs a second person, exactly as a signature does.'
        : problems.length > 0
          ? 'This version has ' + problems.length + ' problem' + (problems.length === 1 ? '' : 's') + ' to fix first.'
          : ''

  return (
    <section className="studio">
      <header className="section-head">
        <h1>Onboarding process</h1>
      </header>

      {/* The API's notes are kept word for word behind the (i) beside the
          short line they explain. */}
      {listNote && (
        <p className="note sw-row">
          {versions.length === 0 && <Status tone="neutral">Shipped default</Status>}
          <ReasonTip label="About the process in force" text={listNote} />
        </p>
      )}
      {writeError && <ErrorBox error={writeError} what="That change" verb="save" />}
      {promoted && (
        <p className="ok-note sw-row">
          <span>Version {promoted.versionNo} is in force</span>
          <ReasonTip label="What putting it into service changes" text={promoted.note} />
        </p>
      )}

      <div className="chiprow">
        {versions.map((v) => (
          <button
            key={v.id}
            type="button"
            className={v.id === openId ? 'chip-toggle chip-on' : 'chip-toggle'}
            onClick={() => setOpenId(v.id)}
          >
            <Status tone={STATUS_WORD[v.status]?.tone ?? 'neutral'}>
              {STATUS_WORD[v.status]?.word ?? v.status.toLowerCase()}
            </Status>{' '}
            version {v.versionNo}
          </button>
        ))}
        {mayWrite && (
          <button
            type="button"
            data-action="tprm.onboarding-process.a02"
            disabled={busy !== ''}
            onClick={() => startDraft(openId ?? undefined)}
          >
            {busy === 'draft' ? 'Drafting' : versions.length === 0 ? 'Draft from the shipped process' : 'New draft'}
          </button>
        )}
      </div>

      {versionError && <ErrorBox error={versionError} what="That version" />}

      {/* A tenant that has authored nothing still has a process, and it is worth
          reading before anybody drafts from it. */}
      {versions.length === 0 && shipped && <StepList steps={shipped.steps} />}

      {version && draft && (
        <>
          <p className="muted">
            {/* A version the seed installed has "seed" against every name on it.
                Printing that verbatim reads as a bug; saying what actually
                happened does not. */}
            {version.createdBy === 'seed'
              ? 'Installed at setup on ' + shortDate(version.createdAt)
              : 'Drafted by ' + version.createdBy + ' on ' + shortDate(version.createdAt) +
                (version.activatedBy ? ', in service by ' + version.activatedBy : '')}
            {version.contentSha256 ? ', fingerprint ' + version.contentSha256.slice(0, 12) : ''}
          </p>

          {problems.length > 0 && (
            <div className="note">
              <strong>
                {problems.length} problem{problems.length === 1 ? '' : 's'} to fix before this can be promoted
              </strong>
              <ul>
                {problems.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            </div>
          )}

          {isDraft && mayWrite ? (
            <StepEditor steps={draft.steps} onEdit={edit} onRemove={removeStep} onAdd={addOwnStep} />
          ) : (
            <StepList steps={draft.steps} />
          )}

          <div className="chiprow" style={{ marginTop: 12 }}>
            {isDraft && mayWrite && (
              <button
                type="button"
                className="primary"
                data-action="tprm.onboarding-process.a01"
                disabled={busy !== '' || !dirty}
                onClick={save}
              >
                {busy === 'save' ? 'Saving' : 'Save draft'}
              </button>
            )}
            <button
              type="button"
              data-action="tprm.onboarding-process.a03"
              disabled={busy !== '' || promoteRefusal !== ''}
              title={promoteRefusal || undefined}
              onClick={promote}
            >
              {busy === 'promote' ? 'Promoting' : 'Put into service'}
            </button>
          </div>
          {savedNote && <p className="ok-note">{savedNote}</p>}
        </>
      )}
    </section>
  )
}

function satisfactionWords(s: OnboardingStepDef): string {
  switch (s.satisfiedBy) {
    case 'OBSERVED':
      return 'Only what the product can see counts'
    case 'EITHER':
      return 'The product seeing it, or a person recording it, either one'
    default:
      return 'Only a person recording it, because the product cannot see this step'
  }
}

function StepList({ steps }: { steps: OnboardingStepDef[] }) {
  return (
    <ol className="act-list">
      {steps.map((s) => (
        <li key={s.key} className={'act-item ' + (s.mandatory ? 'act-unmet' : 'act-off')}>
          <div className="act-head">
            {s.mandatory ? <Status tone="warn">Activation waits</Status> : <Status tone="plain">Not required</Status>}
            <strong>{s.name || <span className="muted">unnamed step</span>}</strong>
            <span className="mono act-code">{s.key}</span>
          </div>
          <p className="act-detail">{satisfactionWords(s)}</p>
          {s.why && <Why>{s.why}</Why>}
        </li>
      ))}
    </ol>
  )
}

function StepEditor({
  steps,
  onEdit,
  onRemove,
  onAdd,
}: {
  steps: OnboardingStepDef[]
  onEdit: (index: number, patch: Partial<OnboardingStepDef>) => void
  onRemove: (index: number) => void
  onAdd: () => void
}) {
  return (
    <>
      <ol className="act-list">
        {steps.map((s, i) => {
          const own = isOwnStep(s)
          return (
            <li key={s.key + ':' + i} className={'act-item ' + (s.mandatory ? 'act-unmet' : 'act-off')}>
              <div className="act-head">
                <strong>{own ? 'Your own step' : s.name}</strong>
                <span className="mono act-code">{s.key}</span>
              </div>

              {own && (
                <div className="form-grid">
                  <div className="field">
                    <label htmlFor={'name-' + i}>What it is called</label>
                    <input
                      id={'name-' + i}
                      value={s.name}
                      onChange={(e) => onEdit(i, { name: e.target.value })}
                      placeholder="Purchase requisition raised"
                    />
                  </div>
                  <div className="field">
                    <label htmlFor={'why-' + i}>Why it is in the process</label>
                    <input
                      id={'why-' + i}
                      value={s.why}
                      onChange={(e) => onEdit(i, { why: e.target.value })}
                      placeholder="Raised in our procurement system before anybody opens a record here."
                    />
                  </div>
                </div>
              )}

              <div className="chiprow" style={{ marginTop: 8 }}>
                <label className="chip-toggle">
                  <input
                    type="checkbox"
                    checked={s.mandatory}
                    onChange={(e) => onEdit(i, { mandatory: e.target.checked })}
                  />{' '}
                  Activation waits for this
                </label>

                {/* Only offered where it means anything. A step the product
                    cannot see has no observation to add, and the API refuses a
                    watched step that stops counting observation. */}
                {!own && (
                  <label className="chip-toggle">
                    <input
                      type="checkbox"
                      checked={s.satisfiedBy === 'EITHER'}
                      onChange={(e) => onEdit(i, { satisfiedBy: e.target.checked ? 'EITHER' : 'OBSERVED' })}
                    />{' '}
                    A person may record it when we do it elsewhere
                  </label>
                )}

                {own && (
                  <button type="button" onClick={() => onRemove(i)}>
                    Remove
                  </button>
                )}
              </div>

              <p className="act-detail muted">{satisfactionWords(s)}</p>
              {!own && s.why && <Why>{s.why}</Why>}
            </li>
          )
        })}
      </ol>

      <div className="chiprow" style={{ marginTop: 8 }}>
        <button type="button" onClick={onAdd}>
          Add a step we do outside this product
        </button>
      </div>
    </>
  )
}
